const POLAND_TIME_ZONE = 'Europe/Warsaw';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const TIME_ZONE_SUFFIX_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;

const polishDateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: POLAND_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const pad = (value, length = 2) => String(value).padStart(length, '0');

const getPolishParts = (date) => Object.fromEntries(
  polishDateTimeFormatter.formatToParts(date)
    .filter(({ type }) => type !== 'literal')
    .map(({ type, value }) => [type, Number(value)])
);

const normalizeMilliseconds = (value = '') => Number(String(value).padEnd(3, '0'));

const formatLocalDateTime = (parts, milliseconds = 0) => (
  `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}.${pad(milliseconds, 3)}`
);

const parseLocalDateTime = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.match(LOCAL_DATE_TIME_PATTERN);
  if (!match) return null;

  const [, year, month, day, hour, minute, second = '0', milliseconds = '0'] = match;
  const parts = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
  const localTimestamp = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    normalizeMilliseconds(milliseconds)
  );

  return Number.isFinite(localTimestamp) ? { parts, localTimestamp } : null;
};

/**
 * Converts a UTC timestamp returned by the API into a Polish civil datetime
 * (`YYYY-MM-DDTHH:mm:ss.SSS`) used by the UI. Date-only values are preserved.
 */
export const utcToPolishTime = (value) => {
  if (value instanceof Date) return formatLocalDateTime(getPolishParts(value), value.getUTCMilliseconds());
  if (typeof value !== 'string' || DATE_ONLY_PATTERN.test(value)) return value;

  // API datetimes without an explicit offset are part of the API's UTC contract.
  const utcValue = TIME_ZONE_SUFFIX_PATTERN.test(value) ? value : `${value}Z`;
  const date = new Date(utcValue);
  if (Number.isNaN(date.getTime())) return value;

  return formatLocalDateTime(getPolishParts(date), date.getUTCMilliseconds());
};

/**
 * Converts a Polish civil datetime from the UI into an ISO-8601 UTC timestamp
 * required by the API. Date-only values are preserved because they are calendar
 * filters, not instants in time.
 */
export const polishTimeToUtc = (value) => {
  if (value instanceof Date) return value.toISOString();
  if (typeof value !== 'string' || DATE_ONLY_PATTERN.test(value) || TIME_ZONE_SUFFIX_PATTERN.test(value)) return value;

  const parsed = parseLocalDateTime(value);
  if (!parsed) return value;

  // Start with the civil time interpreted as UTC, then correct it by Warsaw's
  // offset at that instant. This follows CET/CEST including daylight saving.
  const displayedParts = getPolishParts(new Date(parsed.localTimestamp));
  const displayedTimestamp = Date.UTC(
    displayedParts.year,
    displayedParts.month - 1,
    displayedParts.day,
    displayedParts.hour,
    displayedParts.minute,
    displayedParts.second
  );
  const utcDate = new Date(parsed.localTimestamp + (parsed.localTimestamp - displayedTimestamp));

  return Number.isNaN(utcDate.getTime()) ? value : utcDate.toISOString();
};

const isDateTimeField = (key) => /(?:date|datetime|timestamp|at|on|utc)$/i.test(key);

const isBinaryPayload = (value) => (
  (typeof FormData !== 'undefined' && value instanceof FormData)
  || (typeof Blob !== 'undefined' && value instanceof Blob)
);

const mapDateTimes = (value, converter, key = '') => {
  if (Array.isArray(value)) return value.map((item) => mapDateTimes(item, converter, key));
  if (!value || typeof value !== 'object' || value instanceof Date || isBinaryPayload(value)) return value;

  return Object.entries(value).reduce((result, [entryKey, entryValue]) => {
    result[entryKey] = isDateTimeField(entryKey)
      ? converter(entryValue)
      : mapDateTimes(entryValue, converter, entryKey);
    return result;
  }, {});
};

export const mapApiUtcToPolishTime = (payload) => mapDateTimes(payload, utcToPolishTime);
export const mapPolishTimeToApiUtc = (payload) => mapDateTimes(payload, polishTimeToUtc);
